import React from 'react';
import classnames from 'classnames';
import SuccessIcon from '../../Icons/Success';
import { IconSize, Props as IconProps } from '../../Icons/IconWrap';

import s from './InputLayout.scss';

const bindOwnClassnamesWithSkin = (ownClassMapping: object, skinClassMapping: object) => {
  return (...classnamesList) => {
    const ownClassnames = classnamesList.map(classname => ownClassMapping[classname]);
    const skinClassnames = classnamesList.map(classname => skinClassMapping[classname]);
    return classnames(...ownClassnames, ...skinClassnames);
  };
};

export interface Props {
  className?: string;
  textUnderClassName?: string;
  labelClassName?: string;
  fullWidth?: boolean;
  fullHeight?: boolean;
  label?: string;
  id?: string;
  isFilled: boolean;
  isFocused: boolean;
  hasPlaceholder?: boolean;
  hasError?: boolean;
  readOnly?: boolean;
  success?: boolean;
  loading?: boolean;
  hint?: string;
  prefixIcon?: React.ComponentType<IconProps>;
  withFadedPrefixIcon?: boolean;
  suffix?: React.ReactNode;
  errorMessage?: string | null;
  showHintOnFocus?: boolean;
  withPaddings?: boolean;
  withBar?: boolean; // line below
  skin?: any;
  onClick?(event): void;
}

const InputLayout: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  textUnderClassName,
  labelClassName,
  fullWidth,
  fullHeight,
  label,
  id,
  isFilled,
  isFocused,
  hasPlaceholder,
  hasError,
  readOnly,
  success,
  loading,
  hint,
  prefixIcon: PrefixIcon,
  withFadedPrefixIcon,
  suffix,
  errorMessage,
  showHintOnFocus,
  withPaddings = true,
  withBar = true,
  skin,
  onClick,
}) => {
  const classes = bindOwnClassnamesWithSkin(s, skin || {});

  const renderLabel = () => {
    if (!label) {
      return null;
    }
    return (
      <label htmlFor={id} className={classnames(classes('label'), labelClassName)}>
        {label}
      </label>
    );
  };

  const renderPrefix = () => {
    if (!PrefixIcon) {
      return null;
    }
    return (
      <PrefixIcon
        className={classnames(classes('prefix-icon'), withFadedPrefixIcon && classes('faded'))}
      />
    );
  };

  const renderSuffix = () => {
    const suffixChildren: any = [];
    if (suffix) {
      suffixChildren.push(suffix);
    }
    if (success) {
      suffixChildren.push(<SuccessIcon size={IconSize.TINY} />);
    }
    if (!suffixChildren.length) {
      return null;
    }
    return <div className={classes('suffix')}>{React.Children.toArray(suffixChildren)}</div>;
  };

  const renderTextUnder = () => {
    let textUnder;
    if (hint && (!showHintOnFocus || isFocused)) {
      textUnder = <span className={classes('hint-under')}>{hint}</span>;
    }
    if (errorMessage) {
      textUnder = <span className={classes('error-under')}>{errorMessage}</span>;
    }
    if (!textUnder) {
      return null;
    }
    return (
      <div
        onClick={onTextUnderClick}
        className={classnames(textUnderClassName, classes('text-under'))}
      >
        {textUnder}
      </div>
    );
  };

  const onTextUnderClick = e => {
    // clicking text under should not trigger onClick event -- it's not the control element
    e.stopPropagation();
  };

  return (
    <div
      className={classnames(
        classes('root'),
        withPaddings && classes('input-layout-paddings'),
        fullWidth && s['full-width'],
        s['updated-inputs'],
        className
      )}
    >
      <div
        className={classes(
          'input-layout-content',
          isFocused && 'focused',
          isFilled && 'filled',
          hasPlaceholder && 'has-placeholder',
          PrefixIcon && 'has-prefix-icon',
          hasError && 'has-error',
          success && 'success',
          loading && 'loading',
          readOnly && 'read-only',
          fullHeight && 'full-height'
        )}
        onClick={onClick}
      >
        {renderLabel()}
        <div className={classes('input-wrapper')}>
          {renderPrefix()}
          {children}
          {renderSuffix()}
        </div>
        {withBar && <div className={classes('bar')} />}
        {renderTextUnder()}
      </div>
    </div>
  );
};

export default InputLayout;
